import React, {useEffect, useState} from "react";
import {DocumentIcon} from "@heroicons/react/24/outline";
import styles from "./AdminDocumentInfo.module.css";
import {confirmAPI, getConfirmAPI} from "../../../services/admin";
import {useNavigate} from "react-router-dom";
import DenyConfirmPopUp from "../../../views/admin/popup/DenyConfirmPopUp";
import {ReportStatus} from "../../../types/document";
import Button from "../../common/button/Button";
import {GetConfirmResponseData} from "../../../types/admin";
import ConfirmFilePopUp from "../../../views/admin/popup/ConfirmFilePopUp";
import WriteRemarksPopUp from "../../../views/admin/popup/WriteRemarksPopUp";

interface AdminDocumentInfoProps {
    reportId: number;
}

//어드민 자동 생성 보고서 정보
const AdminDocumentInfo: React.FC<AdminDocumentInfoProps> = ({reportId}) => {
    const [data, setData] = useState<GetConfirmResponseData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
    const [isFileModalOpen, setIsFileModalOpen] = useState(false);
    const navigate = useNavigate();

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //보고서 조회 API
            const documentResponse = await getConfirmAPI(reportId);
            setData(documentResponse);
        } catch (error) {
            console.error('Error fetching document:', error);
        } finally {
            setLoading(false);
        }
    };

    //보고서 파일 미리보기 팝업 열기
    const handleOverView = () => {
        setIsFileModalOpen(true);
    };
    //보고서 파일 미리보기 팝업 닫기
    const handleFileModalClose = () => {
        setIsFileModalOpen(false);
    };
    //보고서 반려 팝업 열기
    const handleModalOpen = () => {
        setIsModalOpen(true);
    }
    //보고서 반려 팝업 닫기
    const handleModalClose = () => {
        setIsModalOpen(false);
        fetchData();
    }
    //보고서 특이사항 작성 팝업 열기
    const handleRemarkModalOpen = () => {
        setIsRemarkModalOpen(true);
    }
    //보고서 특이사항 작성 팝업 닫기
    const handleRemarkModalClose = () => {
        setIsRemarkModalOpen(false);
        fetchData();
    }

    useEffect(() => {
        fetchData();
    }, [reportId]);

    //날짜 형식 변경
    const formatDate = (date: Date) => {
        const d = new Date(date);
        return `${d.getFullYear().toString()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} (${['일', '월', '화', '수', '목', '금', '토'][d.getDay()]})`;
    }
    //보고서 승인
    const handleAccess = async () => {
        try {
            //보고서 처리 API
            await confirmAPI({
                reportId,
                reportStatus: ReportStatus.승인 //승인
            });
            //문서 테이블 페이지로 이동
            navigate("/admin/documents")
        } catch (error) {
            console.error('Error:', error);
        }
    }

    if (loading || !data) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.documentCard}>
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        {/*보고서 제목*/}
                        <h1 className={styles.title}>
                            {data.title}
                        </h1>
                        {/*보고서 생성일자*/}
                        <div className={styles.dateContainer}>
                            <span>{formatDate(data.createdAt)}</span>
                        </div>
                    </div>
                    {/*승인되지 않은 보고서인 경우*/}
                    {data.status !== ReportStatus.승인 && (
                        <div className={styles.actionButtons}>
                            <div className={styles.editDeleteButtons}>
                                {/*특이사항 작성 버튼*/}
                                <Button
                                    variant="primary"
                                    onClick={handleRemarkModalOpen}>
                                    특이사항 작성
                                </Button>
                                {/*승인 버튼*/}
                                <Button
                                    variant="primary"
                                    onClick={handleAccess}>
                                    승인
                                </Button>
                                {/*반려 버튼*/}
                                <Button
                                    variant="primary"
                                    onClick={handleModalOpen}>
                                    반려
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.content}>
                    <div className={styles.documentText}>
                        {/*내용*/}
                        {data.content}
                    </div>
                </div>

                {/*첨부 파일*/}
                {data.fileName && (
                    <div className={styles.fileSection}>
                        <span>
                            <DocumentIcon className={styles.fileIcon}/>
                            <span className={styles.fileName}>{data.fileName}</span>
                            {/*보고서 PDF 미리보기 버튼*/}
                            <Button
                                variant="primary"
                                onClick={handleOverView}
                            >
                                미리 보기
                            </Button>
                        </span>
                    </div>
                )}
            </div>
            {/*보고서 반려 팝업*/}
            <DenyConfirmPopUp
                isOpen={isModalOpen}
                onClose={handleModalClose}
                reportId={reportId}/>
            {/*보고서 PDF 미리보기 팝업*/}
            <ConfirmFilePopUp
                isOpen={isFileModalOpen}
                onClose={handleFileModalClose}
                reportId={reportId}
            />
            {/*특이사항 작성 팝업*/}
            <WriteRemarksPopUp
                isOpen={isRemarkModalOpen}
                onClose={handleRemarkModalClose}
                reportId={reportId}
            />
        </div>
    );
}

export default AdminDocumentInfo;